import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, Send } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Drawer,
  Spinner,
  TextArea,
  TextField,
  useOverlayState,
  type OverlayTriggerState,
} from '@heroui/react';
import {
  aiKeys,
  deleteAiConversation,
  getAiConversationMessages,
  getAiConversations,
  postAiChat,
  type AiBulkRequestCard,
  type AiMisuseCard,
  type AiOwnedSpotCard,
  type AiUsageCard,
  type AiWindowCard,
} from '../../api/ai';
import {
  availableSpotsKeys,
  createBulkSpotUsageRequest,
  createSpotUsage,
  type AvailableSpot,
} from '../../api/available-spots';
import { misuseReportKeys, resolveMisuseReport, type RecentMisuseReport } from '../../api/misuse-reports';
import {
  approveSpotUsage,
  cancelAvailabilityWindow,
  cancelAvailabilityWindowSeries,
  cancelBulkRequest,
  cancelSpotUsage,
  createAvailabilityWindow,
  getAvailabilityWindowsBySpot,
  getMyParkingSpots,
  getMySpotUsages,
  myParkingSpotsKeys,
  rejectSpotUsage,
  type AvailabilityWindow,
  type ParkingSpotOwner,
} from '../../api/my-parking-spots';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  assistantDrawerDialogClassName,
  drawerFieldClassName,
  drawerFooterClassName,
} from '../../components/ui/side-drawer-classes';
import { StatusCallout } from '../../components/ui/status-callout';
import { getErrorMessage, showErrorFromUnknown, showSuccess } from '../../lib/toast';
import { invalidateParkingLists, parkingListQueryOptions } from '../../lib/parking-query';
import { BulkRequestDrawer } from '../available-spots/components/bulk-request-drawer';
import { CancelRequestModal } from '../available-spots/components/cancel-request-modal';
import { ReportMisuseDrawer } from '../available-spots/components/report-misuse-drawer';
import {
  AlreadyResolvedMisuseDialog,
  WillRemoveMisuseDialog,
} from '../available-spots/components/resolve-misuse-dialogs';
import { RequestUsageDrawer } from '../available-spots/components/request-usage-drawer';
import { AvailabilityWindowDrawer } from '../my-parking-spots/components/availability-window-drawer';
import { ConfirmCancelWindowModal } from '../my-parking-spots/components/confirm-cancel-window-modal';
import { filterAndSortActiveWindows } from '../my-parking-spots/utils/availability-windows';
import { ConfirmBulkCancelModal } from '../spot-usages/components/confirm-bulk-cancel-modal';
import { AssistantConversationList } from './assistant-conversation-list';
import { ASSISTANT_SUCCESS_BUTTON_STYLE } from './assistant-success-style';
import {
  ASSISTANT_BACK_TO_LIST_LABEL,
  ASSISTANT_CONVERSATION_STORAGE_KEY,
  ASSISTANT_SUGGESTIONS,
  ASSISTANT_WELCOME_MESSAGE,
} from './assistant-constants';
import {
  assistantAttachmentsFromMetadata,
  assistantChatMessagesFromApi,
  assistantUsageCanApprove,
  assistantUsageCanCancel,
  assistantUsageCanReject,
  type AssistantChatMessage,
} from './assistant-message';
import { AssistantReply, ChatBubble } from './assistant-reply';

interface AssistantDrawerProps {
  state: OverlayTriggerState;
}

const MOBILE_QUERY = '(max-width: 767px)';

function createWelcomeMessages(): AssistantChatMessage[] {
  return [{ id: 'welcome', role: 'assistant', content: ASSISTANT_WELCOME_MESSAGE }];
}

function readStoredConversationId(): string | null {
  try {
    return sessionStorage.getItem(ASSISTANT_CONVERSATION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredConversationId(conversationId: string | null) {
  try {
    if (conversationId) {
      sessionStorage.setItem(ASSISTANT_CONVERSATION_STORAGE_KEY, conversationId);
      return;
    }
    sessionStorage.removeItem(ASSISTANT_CONVERSATION_STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
}

function windowFromCard(card: AiWindowCard): AvailabilityWindow {
  return {
    id: card.id,
    spotId: card.spotId,
    spotCode: card.spotCode,
    seriesId: card.seriesId,
    startAt: card.startAt,
    endAt: card.endAt,
    allowBorrow: card.allowBorrow,
    allowRent: card.allowRent,
    status: 'OPEN',
    createdAt: card.startAt,
    updatedAt: card.startAt,
  };
}

function misuseFromCard(card: AiMisuseCard): RecentMisuseReport {
  return {
    id: card.id,
    spotId: card.spotId,
    spotCode: card.spotCode,
    licensePlate: card.licensePlate,
    photoUrl: null,
    notes: null,
    createdAt: card.createdAt,
    reporterFirebaseUid: '',
  };
}

export function AssistantDrawer({ state }: AssistantDrawerProps) {
  const queryClient = useQueryClient();
  const requestDrawer = useOverlayState();
  const bulkDrawer = useOverlayState();
  const cancelRequestModal = useOverlayState();
  const lendDrawer = useOverlayState();
  const cancelWindowModal = useOverlayState();
  const cancelBulkModal = useOverlayState();
  const approveModal = useOverlayState();
  const rejectModal = useOverlayState();
  const reportMisuseDrawer = useOverlayState();
  const willRemoveMisuseModal = useOverlayState();
  const alreadyResolvedMisuseModal = useOverlayState();
  const [messages, setMessages] = useState<AssistantChatMessage[]>(createWelcomeMessages);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSpot, setSelectedSpot] = useState<AvailableSpot | null>(null);
  const [lendSpot, setLendSpot] = useState<ParkingSpotOwner | null>(null);
  const [usageToCancel, setUsageToCancel] = useState<string | null>(null);
  const [usageToDecide, setUsageToDecide] = useState<string | null>(null);
  const [windowToCancel, setWindowToCancel] = useState<AiWindowCard | null>(null);
  const [bulkToCancel, setBulkToCancel] = useState<string | null>(null);
  const [misuseToResolve, setMisuseToResolve] = useState<AiMisuseCard | null>(null);
  const [cancelledUsageIds, setCancelledUsageIds] = useState<Set<string>>(() => new Set());
  const [decidedUsageIds, setDecidedUsageIds] = useState<Set<string>>(() => new Set());
  const [cancelledWindowIds, setCancelledWindowIds] = useState<Set<string>>(() => new Set());
  const [cancelledBulkIds, setCancelledBulkIds] = useState<Set<string>>(() => new Set());
  const [resolvedMisuseIds, setResolvedMisuseIds] = useState<Set<string>>(() => new Set());
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [mobilePanel, setMobilePanel] = useState<'list' | 'chat'>('chat');
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  );
  const bottomRef = useRef<HTMLDivElement>(null);
  const restoredOnOpen = useRef(false);

  const showSuggestions = messages.length === 1 && messages[0]?.id === 'welcome';
  const showList = !isMobile || mobilePanel === 'list';
  const showChat = !isMobile || mobilePanel === 'chat';

  const conversationsQuery = useQuery({
    queryKey: aiKeys.conversations(),
    queryFn: getAiConversations,
    enabled: state.isOpen,
  });

  const messagesQuery = useQuery({
    queryKey: aiKeys.messages(selectedConversationId ?? 'new'),
    queryFn: ({ queryKey }) => getAiConversationMessages(String(queryKey[2])),
    enabled: state.isOpen && !!selectedConversationId,
  });

  const myUsagesQuery = useQuery({
    queryKey: availableSpotsKeys.myUsages(),
    queryFn: getMySpotUsages,
    enabled: state.isOpen,
    ...parkingListQueryOptions,
  });

  const ownedSpotsQuery = useQuery({
    queryKey: myParkingSpotsKeys.list(),
    queryFn: getMyParkingSpots,
    enabled: state.isOpen,
    ...parkingListQueryOptions,
  });

  const lendSpotId = lendSpot?.spotId;
  const lendWindowsQuery = useQuery({
    queryKey: myParkingSpotsKeys.windows(lendSpotId ?? 'none'),
    queryFn: () => getAvailabilityWindowsBySpot(lendSpotId!),
    enabled: Boolean(lendSpotId && lendDrawer.isOpen),
    select: filterAndSortActiveWindows,
  });

  const createUsageMutation = useMutation({
    mutationFn: createSpotUsage,
    onSuccess: () => {
      showSuccess('Solicitação enviada. Aguardando aprovação do proprietário.');
      void invalidateParkingLists(queryClient);
    },
    onError: showErrorFromUnknown,
  });

  const cancelUsageMutation = useMutation({
    mutationFn: cancelSpotUsage,
    onSuccess: () => {
      showSuccess('Solicitação cancelada.');
      void invalidateParkingLists(queryClient);
    },
    onError: showErrorFromUnknown,
  });

  const approveUsageMutation = useMutation({
    mutationFn: approveSpotUsage,
    onSuccess: () => {
      showSuccess('Solicitação aprovada.');
      void invalidateParkingLists(queryClient);
    },
    onError: showErrorFromUnknown,
  });

  const rejectUsageMutation = useMutation({
    mutationFn: rejectSpotUsage,
    onSuccess: () => {
      showSuccess('Solicitação rejeitada.');
      void invalidateParkingLists(queryClient);
    },
    onError: showErrorFromUnknown,
  });

  const bulkRequestMutation = useMutation({
    mutationFn: createBulkSpotUsageRequest,
    onSuccess: () => {
      showSuccess('Solicitação em massa enviada. Você será notificado se alguém aceitar.');
      void invalidateParkingLists(queryClient);
    },
    onError: showErrorFromUnknown,
  });

  const createWindowMutation = useMutation({
    mutationFn: createAvailabilityWindow,
    onSuccess: () => {
      showSuccess('Janela de disponibilidade criada.');
      void invalidateParkingLists(queryClient);
    },
    onError: showErrorFromUnknown,
  });

  const cancelWindowMutation = useMutation({
    mutationFn: cancelAvailabilityWindow,
    onSuccess: () => {
      showSuccess('Janela cancelada.');
      void invalidateParkingLists(queryClient);
    },
    onError: showErrorFromUnknown,
  });

  const cancelSeriesMutation = useMutation({
    mutationFn: cancelAvailabilityWindowSeries,
    onSuccess: (data) => {
      showSuccess(
        data.cancelledCount > 1
          ? `${data.cancelledCount} janelas da série foram canceladas.`
          : 'Série recorrente cancelada.',
      );
      void invalidateParkingLists(queryClient);
    },
    onError: showErrorFromUnknown,
  });

  const cancelBulkMutation = useMutation({
    mutationFn: cancelBulkRequest,
    onSuccess: () => {
      showSuccess('Solicitação em massa cancelada.');
      void invalidateParkingLists(queryClient);
    },
    onError: showErrorFromUnknown,
  });

  const resolveMisuseMutation = useMutation({
    mutationFn: ({
      reportId,
      resolution,
      reason,
    }: {
      reportId: string;
      resolution: 'WILL_REMOVE' | 'ALREADY_RESOLVED';
      reason?: string;
    }) => resolveMisuseReport(reportId, resolution, reason),
    onSuccess: () => {
      showSuccess('Relato de mau uso encerrado.');
      void invalidateParkingLists(queryClient);
      void queryClient.invalidateQueries({ queryKey: misuseReportKeys.all });
    },
    onError: showErrorFromUnknown,
  });

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!state.isOpen) {
      restoredOnOpen.current = false;
      return;
    }
    if (restoredOnOpen.current || !conversationsQuery.isFetched) return;
    restoredOnOpen.current = true;

    const conversations = conversationsQuery.data?.conversations ?? [];
    const storedId = readStoredConversationId();
    const storedExists = storedId
      ? conversations.some((item) => item.id === storedId)
      : false;

    if (storedExists && storedId) {
      setSelectedConversationId(storedId);
      setMobilePanel('chat');
      return;
    }

    writeStoredConversationId(null);
    setSelectedConversationId(null);
    setMobilePanel(conversations.length > 0 && isMobile ? 'list' : 'chat');
  }, [conversationsQuery.data, conversationsQuery.isFetched, isMobile, state.isOpen]);

  useEffect(() => {
    if (selectedConversationId) return;
    setMessages(createWelcomeMessages());
  }, [selectedConversationId]);

  useEffect(() => {
    if (!selectedConversationId || sending) return;
    if (!messagesQuery.data || messagesQuery.data.conversationId !== selectedConversationId) {
      return;
    }
    const restored = assistantChatMessagesFromApi(messagesQuery.data.messages);
    setMessages(restored);
  }, [messagesQuery.data, selectedConversationId, sending]);

  useEffect(() => {
    if (!state.isOpen) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending, state.isOpen, showChat]);

  const startNewChat = () => {
    setSelectedConversationId(null);
    writeStoredConversationId(null);
    setMessages(createWelcomeMessages());
    setError(null);
    setMobilePanel('chat');
  };

  const openConversation = async (conversationId: string) => {
    setSelectedConversationId(conversationId);
    writeStoredConversationId(conversationId);
    setError(null);
    setMobilePanel('chat');
    try {
      const data = await queryClient.fetchQuery({
        queryKey: aiKeys.messages(conversationId),
        queryFn: () => getAiConversationMessages(conversationId),
      });
      setMessages(assistantChatMessagesFromApi(data.messages));
    } catch (caught) {
      setMessages([]);
      setError(getErrorMessage(caught, 'Não consegui carregar essa conversa.'));
    }
  };

  const deleteConversation = async (conversationId: string) => {
    try {
      await deleteAiConversation(conversationId);
      await queryClient.invalidateQueries({ queryKey: aiKeys.conversations() });
      queryClient.removeQueries({ queryKey: aiKeys.messages(conversationId) });
      if (selectedConversationId === conversationId) {
        startNewChat();
      }
    } catch (caught) {
      showErrorFromUnknown(caught);
    }
  };

  const sendMessage = async (raw: string) => {
    const message = raw.trim();
    if (!message || sending) return;

    const userMessage: AssistantChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
    };
    setMessages((current) =>
      current[0]?.id === 'welcome' ? [userMessage] : [...current, userMessage],
    );
    setDraft('');
    setError(null);
    setSending(true);

    try {
      const response = await postAiChat({
        message,
        conversationId: selectedConversationId ?? undefined,
      });
      writeStoredConversationId(response.conversationId);
      setSelectedConversationId(response.conversationId);
      if (response.reservationCreated || response.refreshParkingLists) {
        void invalidateParkingLists(queryClient);
      }
      setMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.message,
          ...assistantAttachmentsFromMetadata(response as unknown as Record<string, unknown>),
        },
      ]);
      await queryClient.invalidateQueries({ queryKey: aiKeys.conversations() });
      await queryClient.invalidateQueries({
        queryKey: aiKeys.messages(response.conversationId),
      });
    } catch (caught) {
      setError(getErrorMessage(caught, 'Não consegui falar com o assistente agora.'));
    } finally {
      setSending(false);
    }
  };

  const openReserve = (spot: AvailableSpot) => {
    setSelectedSpot(spot);
    requestDrawer.open();
  };

  const openCancelUsage = (usageId: string) => {
    setUsageToCancel(usageId);
    cancelRequestModal.open();
  };

  const openApproveUsage = (usageId: string) => {
    setUsageToDecide(usageId);
    approveModal.open();
  };

  const openRejectUsage = (usageId: string) => {
    setUsageToDecide(usageId);
    rejectModal.open();
  };

  const openLend = (card: AiOwnedSpotCard) => {
    const owned =
      ownedSpotsQuery.data?.find((spot) => spot.spotId === card.spotId) ??
      ({
        id: card.id,
        userId: 'me',
        userEmail: '',
        userName: '',
        spotId: card.spotId,
        spotCode: card.spotCode,
        spotLocation: card.location ?? undefined,
        spotLevel: card.level ?? undefined,
        spotStatus: 'AVAILABLE',
        spotRentStatusMode: 'MARK_AS_RENTED',
        spotIsActive: true,
        ownershipType: 'OWNER',
        createdAt: new Date().toISOString(),
      } satisfies ParkingSpotOwner);
    setLendSpot(owned);
    lendDrawer.open();
  };

  const openCancelWindow = (windowId: string) => {
    const card = messages
      .flatMap((item) => item.windows ?? [])
      .find((window) => window.id === windowId);
    if (!card) return;
    setWindowToCancel(card);
    cancelWindowModal.open();
  };

  const openCancelBulk = (requestId: string) => {
    setBulkToCancel(requestId);
    cancelBulkModal.open();
  };

  const openWillRemoveMisuse = (report: AiMisuseCard) => {
    setMisuseToResolve(report);
    willRemoveMisuseModal.open();
  };

  const openAlreadyResolvedMisuse = (report: AiMisuseCard) => {
    setMisuseToResolve(report);
    alreadyResolvedMisuseModal.open();
  };

  const canCancelUsage = (usage: AiUsageCard) =>
    assistantUsageCanCancel(usage, myUsagesQuery.data ?? [], cancelledUsageIds);

  const canApproveUsage = (usage: AiUsageCard) =>
    assistantUsageCanApprove(usage, decidedUsageIds);

  const canRejectUsage = (usage: AiUsageCard) =>
    assistantUsageCanReject(usage, decidedUsageIds);

  const canCancelWindow = (window: AiWindowCard) =>
    window.canCancel && !cancelledWindowIds.has(window.id);

  const canCancelBulk = (request: AiBulkRequestCard) =>
    request.canCancel && !cancelledBulkIds.has(request.id);

  const canResolveMisuse = (report: AiMisuseCard) =>
    report.canResolve && !resolvedMisuseIds.has(report.id);

  const markDecided = (usageId: string) => {
    setDecidedUsageIds((current) => new Set(current).add(usageId));
    setCancelledUsageIds((current) => new Set(current).add(usageId));
  };

  return (
    <>
      <Drawer state={state}>
        <Drawer.Backdrop isDismissable>
          <Drawer.Content placement="right">
            <Drawer.Dialog className={assistantDrawerDialogClassName}>
              <Drawer.Header>
                {isMobile && mobilePanel === 'chat' ? (
                  <Button
                    isIconOnly
                    variant="ghost"
                    aria-label={ASSISTANT_BACK_TO_LIST_LABEL}
                    onPress={() => setMobilePanel('list')}
                  >
                    <ChevronLeft size={18} />
                  </Button>
                ) : null}
                <Drawer.Heading>Assistente LinkPark</Drawer.Heading>
                <Drawer.CloseTrigger />
              </Drawer.Header>
              <Drawer.Body className="flex min-h-0 flex-1 flex-row gap-0 overflow-hidden p-0">
                {showList ? (
                  <aside className="flex w-full shrink-0 flex-col border-separator p-4 md:w-56 md:border-r">
                    <AssistantConversationList
                      conversations={conversationsQuery.data?.conversations ?? []}
                      selectedId={selectedConversationId}
                      loading={conversationsQuery.isLoading}
                      onSelect={(conversationId) => void openConversation(conversationId)}
                      onNewChat={startNewChat}
                      onDelete={(conversationId) => void deleteConversation(conversationId)}
                    />
                  </aside>
                ) : null}
                {showChat ? (
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
                      {messagesQuery.isLoading && selectedConversationId ? (
                        <div className="flex items-center gap-2 text-sm text-muted">
                          <Spinner size="sm" />
                          Carregando conversa…
                        </div>
                      ) : null}
                      {messages.map((item) =>
                        item.role === 'user' ? (
                          <ChatBubble key={item.id} role="user" content={item.content} />
                        ) : (
                          <AssistantReply
                            key={item.id}
                            content={item.content}
                            spots={item.spots}
                            usages={item.usages}
                            ownedSpots={item.ownedSpots}
                            windows={item.windows}
                            bulkRequests={item.bulkRequests}
                            misuseReports={item.misuseReports}
                            suggestBulkRequest={item.suggestBulkRequest}
                            suggestReportMisuse={item.suggestReportMisuse}
                            onReserve={openReserve}
                            onCancelUsage={openCancelUsage}
                            canCancelUsage={canCancelUsage}
                            onApproveUsage={openApproveUsage}
                            canApproveUsage={canApproveUsage}
                            onRejectUsage={openRejectUsage}
                            canRejectUsage={canRejectUsage}
                            onLendSpot={openLend}
                            onCancelWindow={openCancelWindow}
                            canCancelWindow={canCancelWindow}
                            onCancelBulk={openCancelBulk}
                            canCancelBulk={canCancelBulk}
                            onWillRemoveMisuse={openWillRemoveMisuse}
                            onAlreadyResolvedMisuse={openAlreadyResolvedMisuse}
                            canResolveMisuse={canResolveMisuse}
                            onBulkRequest={bulkDrawer.open}
                            onReportMisuse={reportMisuseDrawer.open}
                          />
                        ),
                      )}
                      {sending ? (
                        <div className="flex items-center gap-2 self-start rounded-2xl bg-surface-secondary px-3 py-2 text-sm text-muted">
                          <Spinner size="sm" />
                          Consultando…
                        </div>
                      ) : null}
                      {error ? (
                        <StatusCallout status="danger" title="Não foi possível responder">
                          {error}
                        </StatusCallout>
                      ) : null}
                      {showSuggestions && !sending ? (
                        <div className="mt-1 flex flex-col gap-2">
                          {ASSISTANT_SUGGESTIONS.map((suggestion) => (
                            <Button
                              key={suggestion}
                              variant="secondary"
                              size="sm"
                              className="h-auto justify-start whitespace-normal text-left"
                              onPress={() => void sendMessage(suggestion)}
                            >
                              {suggestion}
                            </Button>
                          ))}
                        </div>
                      ) : null}
                      <div ref={bottomRef} />
                    </div>
                    <div className={`${drawerFooterClassName} items-end border-t border-separator p-4`}>
                      <TextField className="min-w-0 flex-1" isDisabled={sending}>
                        <TextArea
                          className={drawerFieldClassName}
                          value={draft}
                          rows={2}
                          placeholder="Pergunte sobre vagas…"
                          aria-label="Mensagem para o assistente"
                          onChange={(event) => setDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                              event.preventDefault();
                              void sendMessage(draft);
                            }
                          }}
                        />
                      </TextField>
                      <Button
                        isIconOnly
                        variant="primary"
                        aria-label="Enviar mensagem"
                        isDisabled={sending || draft.trim().length === 0}
                        onPress={() => void sendMessage(draft)}
                        style={ASSISTANT_SUCCESS_BUTTON_STYLE}
                      >
                        {sending ? <Spinner size="sm" color="current" /> : <Send size={16} />}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </Drawer.Body>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>
      </Drawer>

      <RequestUsageDrawer
        state={requestDrawer}
        spot={selectedSpot}
        myUsages={myUsagesQuery.data ?? []}
        onSubmit={async (request) => {
          await createUsageMutation.mutateAsync(request);
          requestDrawer.close();
        }}
        onCancelUsage={async (usageId) => {
          await cancelUsageMutation.mutateAsync(usageId);
          setCancelledUsageIds((current) => new Set(current).add(usageId));
        }}
      />
      <BulkRequestDrawer
        state={bulkDrawer}
        onSubmit={async (payload) => {
          await bulkRequestMutation.mutateAsync(payload);
        }}
      />
      <CancelRequestModal
        state={cancelRequestModal}
        onConfirm={async () => {
          if (!usageToCancel) return;
          await cancelUsageMutation.mutateAsync(usageToCancel);
          setCancelledUsageIds((current) => new Set(current).add(usageToCancel));
          setUsageToCancel(null);
        }}
      />
      <ConfirmDialog
        state={approveModal}
        title="Aprovar solicitação"
        cancelLabel="Manter pendente"
        confirmLabel="Sim, aprovar"
        confirmDisabled={!usageToDecide}
        onConfirm={async () => {
          if (!usageToDecide) return;
          await approveUsageMutation.mutateAsync(usageToDecide);
          markDecided(usageToDecide);
          setUsageToDecide(null);
        }}
        message={
          <p className="text-muted">
            Tem certeza que deseja aprovar esta solicitação de uso da vaga?
          </p>
        }
      />
      <ConfirmDialog
        state={rejectModal}
        title="Rejeitar solicitação"
        cancelLabel="Manter pendente"
        confirmLabel="Sim, rejeitar"
        confirmVariant="danger-soft"
        confirmDisabled={!usageToDecide}
        onConfirm={async () => {
          if (!usageToDecide) return;
          await rejectUsageMutation.mutateAsync(usageToDecide);
          markDecided(usageToDecide);
          setUsageToDecide(null);
        }}
        message={
          <p className="text-muted">
            Tem certeza que deseja rejeitar esta solicitação de uso da vaga?
          </p>
        }
      />
      <AvailabilityWindowDrawer
        state={lendDrawer}
        spot={lendSpot}
        activeWindows={lendWindowsQuery.data ?? []}
        editingWindow={null}
        onSubmit={async (payload) => {
          if ('windowId' in payload) return;
          await createWindowMutation.mutateAsync(payload);
          lendDrawer.close();
        }}
      />
      <ConfirmCancelWindowModal
        state={cancelWindowModal}
        window={windowToCancel ? windowFromCard(windowToCancel) : null}
        onConfirm={async () => {
          if (!windowToCancel) return;
          await cancelWindowMutation.mutateAsync(windowToCancel.id);
          setCancelledWindowIds((current) => new Set(current).add(windowToCancel.id));
          setWindowToCancel(null);
        }}
        onConfirmSeries={
          windowToCancel?.seriesId
            ? async () => {
                await cancelSeriesMutation.mutateAsync(windowToCancel.seriesId!);
                setCancelledWindowIds((current) => new Set(current).add(windowToCancel.id));
                setWindowToCancel(null);
              }
            : undefined
        }
      />
      <ConfirmBulkCancelModal
        state={cancelBulkModal}
        onConfirm={async () => {
          if (!bulkToCancel) return;
          await cancelBulkMutation.mutateAsync(bulkToCancel);
          setCancelledBulkIds((current) => new Set(current).add(bulkToCancel));
          setBulkToCancel(null);
        }}
      />
      <ReportMisuseDrawer
        state={reportMisuseDrawer}
        spots={(ownedSpotsQuery.data ?? []).map((spot) => ({
          id: spot.spotId,
          code: spot.spotCode,
        }))}
        onSubmitted={() => {
          void invalidateParkingLists(queryClient);
          void queryClient.invalidateQueries({ queryKey: misuseReportKeys.all });
        }}
      />
      <AlreadyResolvedMisuseDialog
        state={alreadyResolvedMisuseModal}
        question="O carro já foi retirado da vaga?"
        onConfirm={async () => {
          if (!misuseToResolve) return;
          await resolveMisuseMutation.mutateAsync({
            reportId: misuseToResolve.id,
            resolution: 'ALREADY_RESOLVED',
          });
          setResolvedMisuseIds((current) => new Set(current).add(misuseToResolve.id));
          setMisuseToResolve(null);
        }}
      />
      <WillRemoveMisuseDialog
        state={willRemoveMisuseModal}
        report={
          misuseToResolve
            ? misuseFromCard(misuseToResolve)
            : {
                id: '',
                spotId: '',
                spotCode: '',
                licensePlate: '',
                photoUrl: null,
                notes: null,
                createdAt: new Date().toISOString(),
                reporterFirebaseUid: '',
              }
        }
        onConfirm={async (reason) => {
          if (!misuseToResolve) return;
          await resolveMisuseMutation.mutateAsync({
            reportId: misuseToResolve.id,
            resolution: 'WILL_REMOVE',
            reason,
          });
          setResolvedMisuseIds((current) => new Set(current).add(misuseToResolve.id));
          setMisuseToResolve(null);
        }}
      />
    </>
  );
}
